import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userApi, type UserNotification } from '../../services/userApi';

const CHANNEL_LABELS: Record<UserNotification['channel'], string> = {
  email: 'Email',
  in_app: 'In-app',
};

/** Best-available timestamp for a notification: when it was sent, else created. */
function notificationTimestamp(n: UserNotification): string | null {
  return n.sentAt ?? n.createdAt ?? null;
}

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function notificationLabel(n: UserNotification): string {
  return n.subject ?? n.body ?? 'Notification';
}

/**
 * Notifications history page (issue #5).
 *
 * Lists the signed-in user's past notifications (email + in-app) written by
 * the notifier worker, most recent first. Each row shows the delivery
 * channel, subject/message, and timestamp, with a Delete action guarded by a
 * confirm dialog — mirroring the SavedSearchesPage / AlertsPage pattern. The
 * list is user-scoped server-side; the page never surfaces another user's
 * notifications.
 */
export function NotificationsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['user', 'notifications'],
    queryFn: () => userApi.listNotifications(),
  });

  const [confirmDelete, setConfirmDelete] = useState<UserNotification | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => userApi.deleteNotification(id),
    onSuccess: () => {
      setConfirmDelete(null);
      return queryClient.invalidateQueries({ queryKey: ['user', 'notifications'] });
    },
  });

  const notifications = query.data?.notifications ?? [];

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Notifications
      </Typography>

      {query.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading notifications" />
        </Box>
      ) : query.isError ? (
        <Alert severity="error" role="alert">
          Failed to load notifications: {(query.error as Error).message}
        </Alert>
      ) : notifications.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1">No notifications yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            When an alert fires you’ll see it here. You can also choose to receive notifications by
            email — set this per alert on the Alerts page.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="Your notifications">
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell>Notification</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map((n) => (
                <TableRow key={n.id} hover>
                  <TableCell>
                    <Chip size="small" label={CHANNEL_LABELS[n.channel] ?? n.channel} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {notificationLabel(n)}
                    </Typography>
                    {n.subject && n.body ? (
                      <Typography variant="body2" color="text.secondary">
                        {n.body}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{formatTimestamp(notificationTimestamp(n))}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setConfirmDelete(n)}
                        aria-label={`Delete notification ${notificationLabel(n)}`}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="confirm-delete-notification-title"
      >
        <DialogTitle id="confirm-delete-notification-title">Delete this notification?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{confirmDelete ? notificationLabel(confirmDelete) : ''}” will be removed from your
            notification history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NotificationsPage;
